import React, { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
type Expenses = {
  housing: number;
  transport: number;
  food: number;
  utilities: number;
  entertainment: number;
  savings: number;
  other: number;
};
type Frequency = 'weekly' | 'fortnightly' | 'monthly' | 'yearly';
const COLORS = ['#3b82f6', '#06b6d4', '#14b8a6', '#8b5cf6', '#f59e0b', '#10b981', '#64748b'];
const FREQUENCY_OPTIONS: {
  value: Frequency;
  label: string;
}[] = [{
  value: 'weekly',
  label: 'Weekly'
}, {
  value: 'fortnightly',
  label: 'Fortnightly'
}, {
  value: 'monthly',
  label: 'Monthly'
}, {
  value: 'yearly',
  label: 'Yearly'
}];
const toMonthly = (amount: number, freq: Frequency): number => {
  switch (freq) {
    case 'weekly':
      return amount * 52 / 12;
    case 'fortnightly':
      return amount * 26 / 12;
    case 'monthly':
      return amount;
    case 'yearly':
      return amount / 12;
  }
};
const fromMonthly = (amount: number, freq: Frequency): number => {
  switch (freq) {
    case 'weekly':
      return amount * 12 / 52;
    case 'fortnightly':
      return amount * 12 / 26;
    case 'monthly':
      return amount;
    case 'yearly':
      return amount * 12;
  }
};
function FrequencyTabs({
  value,
  onChange
}: {
  value: Frequency;
  onChange: (freq: Frequency) => void;
}) {
  return <div className="flex rounded-lg bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-0.5" data-id="element-448">
      {FREQUENCY_OPTIONS.map(opt => <button key={opt.value} onClick={() => onChange(opt.value)} className={`flex-1 text-xs font-medium py-1.5 px-1 rounded-md transition-all ${value === opt.value ? 'bg-white dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`} data-id="element-449">
          {opt.label}
        </button>)}
    </div>;
}
export function BudgetPlanner() {
  const [incomeFrequency, setIncomeFrequency] = useState<Frequency>('monthly');
  const [expenseFrequency, setExpenseFrequency] = useState<Frequency>('monthly');
  // Store values in monthly terms internally
  const [monthlyIncome, setMonthlyIncome] = useState<number>(8000);
  const [monthlyExpenses, setMonthlyExpenses] = useState<Expenses>({
    housing: 2500,
    transport: 600,
    food: 1200,
    utilities: 400,
    entertainment: 500,
    savings: 1500,
    other: 300
  });
  // Display values converted to the selected frequency
  const displayIncome = Math.round(fromMonthly(monthlyIncome, incomeFrequency));
  const getDisplayExpense = (key: keyof Expenses) => Math.round(fromMonthly(monthlyExpenses[key], expenseFrequency));
  const handleIncomeChange = (displayValue: number) => {
    setMonthlyIncome(toMonthly(displayValue, incomeFrequency));
  };
  const handleExpenseChange = (key: keyof Expenses, displayValue: number) => {
    setMonthlyExpenses(prev => ({
      ...prev,
      [key]: toMonthly(displayValue, expenseFrequency)
    }));
  };
  const results = useMemo(() => {
    const totalExpenses = Object.values(monthlyExpenses).reduce((a, b) => a + b, 0);
    const remaining = monthlyIncome - totalExpenses;
    const chartData = [{
      name: 'Housing',
      value: monthlyExpenses.housing
    }, {
      name: 'Transport',
      value: monthlyExpenses.transport
    }, {
      name: 'Food',
      value: monthlyExpenses.food
    }, {
      name: 'Utilities',
      value: monthlyExpenses.utilities
    }, {
      name: 'Entertainment',
      value: monthlyExpenses.entertainment
    }, {
      name: 'Savings',
      value: monthlyExpenses.savings
    }, {
      name: 'Other',
      value: monthlyExpenses.other
    }].filter(item => item.value > 0);
    return {
      totalExpenses,
      remaining,
      chartData
    };
  }, [monthlyIncome, monthlyExpenses]);
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  }).format(val);
  const frequencyLabel = (freq: Frequency) => {
    switch (freq) {
      case 'weekly':
        return 'Weekly';
      case 'fortnightly':
        return 'Fortnightly';
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
    }
  };
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-450">
      <div className="lg:col-span-1 space-y-6 bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-slate-200 dark:border-white/10 max-h-[700px] overflow-y-auto custom-scrollbar" data-id="element-451">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4" data-id="element-452">
          Budget Planner
        </h3>

        {/* Income Section */}
        <div className="space-y-3" data-id="element-453">
          <div className="flex items-center justify-between" data-id="element-454">
            <label className="text-sm font-medium text-primary-600 dark:text-primary-400" data-id="element-455">
              Income (After Tax)
            </label>
          </div>
          <FrequencyTabs value={incomeFrequency} onChange={setIncomeFrequency} data-id="element-456" />
          <input type="number" value={displayIncome} onChange={e => handleIncomeChange(Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-primary-500/30 dark:border-primary-500/30 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" placeholder={`${frequencyLabel(incomeFrequency)} income`} data-id="element-457" />
          {incomeFrequency !== 'monthly' && <p className="text-xs text-slate-400 dark:text-slate-500" data-id="element-458">
              ≈ {formatCurrency(monthlyIncome)} / month
            </p>}
        </div>

        {/* Expenses Section */}
        <div className="pt-4 border-t border-slate-200 dark:border-white/10" data-id="element-459">
          <div className="flex items-center justify-between mb-3" data-id="element-460">
            <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400" data-id="element-461">
              Expenses
            </h4>
          </div>
          <div className="mb-4" data-id="element-462">
            <FrequencyTabs value={expenseFrequency} onChange={setExpenseFrequency} data-id="element-463" />
          </div>

          {Object.entries(monthlyExpenses).map(([key]) => <div key={key} className="mb-4" data-id="element-464">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 capitalize" data-id="element-465">
                {key}
              </label>
              <input type="number" value={getDisplayExpense(key as keyof Expenses)} onChange={e => handleExpenseChange(key as keyof Expenses, Number(e.target.value))} className="w-full bg-slate-50 dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" data-id="element-466" />
            </div>)}
          {expenseFrequency !== 'monthly' && <p className="text-xs text-slate-400 dark:text-slate-500" data-id="element-467">
              All expenses shown as{' '}
              {frequencyLabel(expenseFrequency).toLowerCase()} amounts. Monthly
              total: {formatCurrency(results.totalExpenses)}
            </p>}
        </div>
      </div>

      {/* Right side - results (unchanged) */}
      <div className="lg:col-span-2 space-y-6" data-id="element-468">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-469">
          <div className="bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-slate-200 dark:border-white/10" data-id="element-470">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1" data-id="element-471">
              Monthly Income
            </p>
            <p className="text-3xl font-bold text-slate-900 dark:text-white" data-id="element-472">
              {formatCurrency(monthlyIncome)}
            </p>
          </div>
          <div className="bg-slate-50 dark:bg-white/5 p-6 rounded-2xl border border-slate-200 dark:border-white/10" data-id="element-473">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1" data-id="element-474">
              Monthly Allocated
            </p>
            <p className="text-3xl font-bold text-accent-500 dark:text-accent-400" data-id="element-475">
              {formatCurrency(results.totalExpenses)}
            </p>
          </div>
          <div className={`p-6 rounded-2xl border ${results.remaining >= 0 ? 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30' : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30'}`} data-id="element-476">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1" data-id="element-477">
              Monthly Remaining
            </p>
            <p className={`text-3xl font-bold ${results.remaining >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-478">
              {formatCurrency(results.remaining)}
            </p>
          </div>
        </div>

        <div className="bg-slate-50 dark:bg-white/5 p-4 sm:p-6 rounded-2xl border border-slate-200 dark:border-white/10 h-[350px] sm:h-[450px] flex flex-col items-center justify-center" data-id="element-479">
          <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-2 w-full text-left" data-id="element-480">
            Allocation Breakdown
          </h4>
          {results.totalExpenses > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-481">
              <PieChart data-id="element-482">
                <Pie data={results.chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" stroke="none" data-id="element-483">
                  {results.chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-484" />)}
                </Pie>
                <Tooltip contentStyle={{
              backgroundColor: '#0f172a',
              borderColor: '#1e293b',
              borderRadius: '8px',
              color: '#fff'
            }} formatter={(value: number) => formatCurrency(value)} data-id="element-485" />
                <Legend layout="vertical" verticalAlign="middle" align="right" data-id="element-486" />
              </PieChart>
            </ResponsiveContainer> : <div className="text-slate-500 flex items-center justify-center h-full" data-id="element-487">
              No expenses allocated
            </div>}
        </div>
      </div>
    </div>;
}